/**
 * ExecutiveDashboardPage — Premium HR cockpit (Admin / SuperAdmin)
 * Style: Linear / Rippling / Deel — light theme, soft shadows, 20px radius
 * Politique: aucune donnée simulée — uniquement des valeurs réelles issues de la DB.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  LineChart, Line, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Tooltip as RTooltip,
} from 'recharts';
import {
  Users, Building2, MapPin, UserCog, ArrowUpRight, ArrowDownRight, Sparkles, TrendingUp,
  TrendingDown, AlertTriangle, ChevronRight, ClipboardCheck, FileSignature, UserPlus,
  Star, Brain, Upload, FileText, Calendar, Clock, Activity,
  Target, BarChart3,
} from 'lucide-react';

// ---- Helpers ----
const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const monthFR = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset);
  return d.toLocaleString('fr-FR', { month: 'short', year: '2-digit' });
};

const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(v)) ? '—' : `${v}%`;
const fmtNumber = (v) => (v === null || v === undefined || Number.isNaN(v)) ? '—' : Number(v).toLocaleString('fr-FR');

// ---- KPI Card ----
function KpiCard({ icon: Icon, label, value, trend, trendUp, color, footLabel, footValue, footPct, testId }) {
  const stroke = { brand: '#2563EB', ok: '#059669', warn: '#D97706', ai: '#7C3AED' }[color] || '#2563EB';
  const bgIcon = { brand: 'bg-blue-100 text-blue-700', ok: 'bg-emerald-100 text-emerald-700',
    warn: 'bg-amber-100 text-amber-700', ai: 'bg-violet-100 text-violet-700' }[color] || 'bg-blue-100 text-blue-700';
  const hasTrend = trend !== null && trend !== undefined && trend !== '';
  const trendCls = trendUp ? 'text-ok bg-ok-soft' : 'text-bad bg-bad-soft';
  const hasFoot = footLabel && footValue !== null && footValue !== undefined && footValue !== '—';
  return (
    <div data-testid={testId}
      className="group relative rounded-[20px] border border-ink-200 bg-white p-5 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-6px_rgba(15,23,42,0.08)]">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`grid h-11 w-11 place-items-center rounded-xl ${bgIcon}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</div>
            <div className="mt-0.5 text-3xl font-semibold tracking-tight text-ink-900">{value}</div>
          </div>
        </div>
        {hasTrend && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${trendCls}`}>
            {trendUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {trend}
          </span>
        )}
      </div>
      {hasFoot && (
        <div className="mt-4 flex items-center justify-between border-t border-ink-100 pt-3">
          <span className="text-xs text-ink-500">{footLabel}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink-900">{footValue}</span>
            {footPct !== null && footPct !== undefined && (
              <div className="h-1.5 w-16 rounded-full bg-ink-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${footPct}%`, backgroundColor: stroke }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- AI Assistant ----
function AIAssistantCard({ insights }) {
  if (!insights || insights.length === 0) {
    return (
      <div data-testid="ai-assistant-card"
        className="flex flex-col overflow-hidden rounded-[20px] border border-ink-200 bg-white shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]">
        <div className="relative overflow-hidden bg-gradient-to-br from-ai-soft via-white to-white p-5 border-b border-ink-100">
          <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-ai/10 blur-3xl" />
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-ai shadow-[0_8px_24px_-6px_rgba(124,58,237,0.5)]">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-base font-semibold text-ink-900">
                Assistant RH IA
                <span className="rounded-md bg-ai px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white">BETA</span>
              </h3>
              <p className="text-xs text-ink-500">Aucun insight à afficher pour le moment</p>
            </div>
          </div>
        </div>
        <div className="flex-1 p-5 text-sm text-ink-500">
          Importez vos employés et lancez une campagne d'évaluation pour faire apparaître ici des insights basés sur vos données.
        </div>
        <div className="px-5 pb-5">
          <Link to="/ai-insights" data-testid="ai-ask-question"
            className="group flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-ai to-ai-hover px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_-6px_rgba(124,58,237,0.5)] transition hover:from-ai-hover hover:to-ai">
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Poser une question à l'IA
            </span>
            <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="ai-assistant-card"
      className="flex flex-col overflow-hidden rounded-[20px] border border-ink-200 bg-white shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]">
      <div className="relative overflow-hidden bg-gradient-to-br from-ai-soft via-white to-white p-5 border-b border-ink-100">
        <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-ai/10 blur-3xl" />
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-ai shadow-[0_8px_24px_-6px_rgba(124,58,237,0.5)]">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-ink-900">
              Assistant RH IA
              <span className="rounded-md bg-ai px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white">BETA</span>
            </h3>
            <p className="text-xs text-ink-500">Insights détectés aujourd'hui</p>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-3 p-5">
        {insights.map((it, i) => (
          <Link key={i} to={it.href || '#'}
            data-testid={`ai-insight-${i}`}
            className="group flex items-start gap-3 rounded-xl border border-transparent p-3 transition hover:border-ink-200 hover:bg-ink-50">
            <div className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg ${it.iconBg}`}>
              <it.icon className={`h-3.5 w-3.5 ${it.iconColor}`} />
            </div>
            <div className="flex-1 text-sm text-ink-700">
              {it.text}
              <span className="ml-1 inline-flex items-center text-xs font-medium text-ai opacity-0 transition group-hover:opacity-100">
                Voir <ChevronRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>
      <div className="px-5 pb-5">
        <Link to="/ai-insights" data-testid="ai-ask-question"
          className="group flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-ai to-ai-hover px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_-6px_rgba(124,58,237,0.5)] transition hover:from-ai-hover hover:to-ai">
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Poser une question à l'IA
          </span>
          <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </div>
  );
}

// ---- Priority actions ----
function PriorityActionsList({ items }) {
  return (
    <div data-testid="priority-actions"
      className="rounded-[20px] border border-ink-200 bg-white p-5 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-ink-900">Actions prioritaires</h3>
        <Link to="/dashboard?tab=todo" className="text-xs font-medium text-brand hover:text-brand-hover">
          Tout voir
        </Link>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <Link key={i} to={it.href}
            data-testid={`priority-${i}`}
            className="group flex items-center gap-3 rounded-xl px-2 py-2.5 transition hover:bg-ink-50">
            <div className={`grid h-9 w-9 place-items-center rounded-lg ${it.bg}`}>
              <it.icon className={`h-4 w-4 ${it.color}`} />
            </div>
            <span className="flex-1 text-sm font-medium text-ink-900">{it.label}</span>
            <span className={`min-w-[24px] rounded-full px-2 py-0.5 text-center text-xs font-bold ${it.badge}`}>
              {it.count}
            </span>
            <ChevronRight className="h-4 w-4 text-ink-300 transition group-hover:text-ink-500 group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---- Quick actions ----
function QuickActionTile({ icon: Icon, label, sub, to, color }) {
  return (
    <Link to={to} data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="group flex flex-col items-start gap-3 rounded-2xl border border-ink-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.1)]">
      <div className={`grid h-11 w-11 place-items-center rounded-xl ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-sm font-semibold text-ink-900">{label}</div>
        <div className="text-xs text-ink-500">{sub}</div>
      </div>
    </Link>
  );
}

// ---- Hero ----
function HeroBanner({ userName, healthScore, criticalCount }) {
  const hasHealth = typeof healthScore === 'number' && !Number.isNaN(healthScore);
  const data = hasHealth ? [{ name: 'health', value: healthScore }, { name: 'rest', value: 100 - healthScore }] : [];
  const color = !hasHealth ? '#94A3B8' : healthScore >= 80 ? '#059669' : healthScore >= 60 ? '#D97706' : '#DC2626';
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
          {greet()}, <span className="text-brand">{userName}</span> <span className="inline-block">👋</span>
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          Voici ce qui se passe aujourd'hui dans votre organisation.
        </p>
      </div>
      <div className="flex items-center gap-3">
        {hasHealth && (
          <div className="flex items-center gap-3 rounded-2xl border border-ink-200 bg-white px-4 py-3 shadow-sm" data-testid="health-score-card">
            <div className="relative h-14 w-14">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="value" innerRadius={20} outerRadius={28} startAngle={90} endAngle={-270} stroke="none">
                    <Cell fill={color} />
                    <Cell fill="#E2E8F0" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center text-xs font-bold text-ink-900">{healthScore}%</div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Qualité des données</div>
              <div className="text-sm font-semibold text-ink-900">
                {healthScore >= 80 ? 'Excellente' : healthScore >= 60 ? 'Correcte' : 'À compléter'}
              </div>
            </div>
          </div>
        )}

        {criticalCount > 0 && (
          <Link to="/dashboard?tab=todo" data-testid="critical-actions-card"
            className="group flex items-center gap-3 rounded-2xl border border-bad/20 bg-bad-soft px-4 py-3 transition hover:border-bad/40">
            <AlertTriangle className="h-5 w-5 text-bad" />
            <div>
              <div className="text-sm font-semibold text-ink-900">{criticalCount} actions critiques</div>
              <div className="text-[11px] text-bad">nécessitent votre attention</div>
            </div>
            <ChevronRight className="h-4 w-4 text-bad opacity-0 transition group-hover:opacity-100" />
          </Link>
        )}
      </div>
    </div>
  );
}

// ---- Charts row ----
function HeadcountEvolution({ data, employees }) {
  // data: array of {m, value} pour les 12 derniers mois calculés depuis les vraies dates d'embauche
  const hasData = Array.isArray(data) && data.length >= 2 && data.some(d => d.value > 0);
  const firstNonZero = hasData ? data.find(d => d.value > 0)?.value : null;
  const last = hasData ? data[data.length - 1].value : null;
  const deltaPct = (firstNonZero && firstNonZero > 0 && last !== null)
    ? Math.round(((last - firstNonZero) / firstNonZero) * 100)
    : null;

  return (
    <div className="rounded-[20px] border border-ink-200 bg-white p-5 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]">
      <div className="mb-2 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-ink-900">Évolution des effectifs</h3>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tracking-tight text-ink-900">{fmtNumber(employees)}</span>
            <span className="text-xs text-ink-500">Total employés</span>
            {deltaPct !== null && (
              <span className={`text-xs font-semibold ${deltaPct >= 0 ? 'text-ok' : 'text-bad'}`}>
                {deltaPct >= 0 ? '+' : ''}{deltaPct}% vs {monthFR(-11)}
              </span>
            )}
          </div>
        </div>
      </div>
      {hasData ? (
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="evol-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={2} fill="url(#evol-grad)"
                dot={{ r: 3, fill: '#2563EB', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 5 }} />
              <RTooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12, boxShadow: '0 12px 32px -8px rgba(15,23,42,0.12)' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-3 h-64 grid place-items-center rounded-xl border border-dashed border-ink-200 bg-ink-50/40 text-center px-6">
          <div className="text-sm text-ink-500">
            <Users className="h-6 w-6 mx-auto mb-2 text-ink-300" />
            <div className="font-medium text-ink-700">Pas encore d'historique d'effectifs</div>
            <div className="text-xs mt-1">Ajoutez des employés avec leur date d'embauche pour voir la courbe.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DepartmentDonut({ breakdown }) {
  const palette = ['#2563EB', '#7C3AED', '#F59E0B', '#059669', '#DC2626', '#6366F1', '#94A3B8'];
  const total = breakdown.reduce((s, b) => s + b.value, 0);
  const hasData = breakdown.length > 0 && total > 0;
  return (
    <div className="rounded-[20px] border border-ink-200 bg-white p-5 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]">
      <h3 className="mb-2 text-base font-semibold text-ink-900">Répartition par département</h3>
      {hasData ? (
        <div className="flex items-center gap-4">
          <div className="relative h-48 w-48 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={breakdown} dataKey="value" innerRadius={56} outerRadius={86} paddingAngle={2} stroke="none">
                  {breakdown.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                </Pie>
                <RTooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="text-2xl font-semibold text-ink-900">{total}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-500">Employés</div>
              </div>
            </div>
          </div>
          <div className="flex-1 space-y-2">
            {breakdown.slice(0, 6).map((b, i) => (
              <div key={b.name} className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: palette[i % palette.length] }} />
                <span className="flex-1 truncate text-ink-700">{b.name}</span>
                <span className="font-semibold text-ink-900">{Math.round((b.value / total) * 100)}%</span>
                <span className="text-ink-500">({b.value})</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="h-48 grid place-items-center rounded-xl border border-dashed border-ink-200 bg-ink-50/40 text-center px-6">
          <div className="text-sm text-ink-500">
            <Building2 className="h-6 w-6 mx-auto mb-2 text-ink-300" />
            <div className="font-medium text-ink-700">Aucun employé rattaché à un département</div>
            <div className="text-xs mt-1">Importez vos employés ou affectez-les à un département pour voir la répartition.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Trends row ----
function TrendKpi({ icon: Icon, label, value, delta, deltaUp, color, testId }) {
  const stroke = { brand: '#2563EB', ok: '#059669', warn: '#D97706', bad: '#DC2626' }[color] || '#2563EB';
  const hasDelta = delta !== null && delta !== undefined && delta !== '';
  return (
    <div data-testid={testId} className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-ink-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="font-medium">{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-ink-900">{value}</span>
        {hasDelta && (
          <span className={`text-xs font-semibold ${deltaUp ? 'text-ok' : 'text-bad'}`}>
            {deltaUp ? '↑' : '↓'} {delta}
          </span>
        )}
      </div>
      <div className="mt-2 h-1 rounded-full bg-ink-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: stroke, opacity: 0.6 }} />
      </div>
    </div>
  );
}

// ---- Main page ----
export default function ExecutiveDashboardPage() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [employees, setEmployees] = useState({ total: 0, list: [] });
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState({ total: 0, active: 0 });
  const [pendingActions, setPendingActions] = useState({ validations: 0, evaluations: 0, contracts: 0, onboarding: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [empRes, deptRes, sitesRes, usersRes, kpiRes] = await Promise.all([
          // Limit suffisamment haut pour récupérer la liste complète pour les agrégations
          api.get('/employees', { params: { limit: 10000 } }).catch(() => ({ data: null })),
          api.get('/departments').catch(() => ({ data: [] })),
          api.get('/sites').catch(() => ({ data: [] })),
          api.get('/users').catch(() => ({ data: null })),
          api.get('/kpi/current').catch(() => ({ data: null })),
        ]);
        if (cancelled) return;

        const empList = Array.isArray(empRes.data)
          ? empRes.data
          : (empRes.data?.employees || empRes.data?.items || []);
        const empTotal = empRes.data?.total ?? empList.length;
        setEmployees({ total: empTotal, list: empList });
        const deptData = deptRes.data;
        setDepartments(Array.isArray(deptData) ? deptData : (deptData?.departments || deptData?.items || []));
        const sitesData = sitesRes.data;
        setSites(Array.isArray(sitesData) ? sitesData : (sitesData?.sites || sitesData?.items || []));
        const usersList = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || usersRes.data?.items || []);
        setUsers({ total: usersList.length, active: usersList.filter(u => u.isActive !== false).length });
        setKpi(kpiRes.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Compteurs réels (chacun retombe à 0 si l'endpoint n'est pas dispo)
    Promise.all([
      api.get('/leave/requests', { params: { status: 'hr_pending' } }).catch(() => ({ data: { items: [] } })),
      api.get('/performance-eval/to-validate').catch(() => ({ data: [] })),
      api.get('/employees', { params: { status: 'Onboarding', limit: 1 } }).catch(() => ({ data: null })),
    ]).then(([leaves, evals, onboarding]) => {
      if (cancelled) return;
      const leavesList = Array.isArray(leaves.data) ? leaves.data : (leaves.data?.items || leaves.data?.requests || []);
      const evalList = Array.isArray(evals.data) ? evals.data : (evals.data?.items || []);
      const onboardingCount = onboarding.data?.total
        ?? (Array.isArray(onboarding.data) ? onboarding.data.length : (onboarding.data?.employees?.length || 0));
      setPendingActions(p => ({
        ...p,
        validations: leavesList.length,
        evaluations: evalList.length,
        onboarding: onboardingCount,
      }));
    });

    return () => { cancelled = true; };
  }, []);

  // Redirects (same logic as legacy page) — placed AFTER all hooks above to respect Rules of Hooks
  const empCount = employees.total || 0;
  const deptCount = departments.length;
  const siteCount = sites.length;
  const userCount = users.total;

  // Répartition réelle par département (depuis employees.departmentId)
  const deptBreakdown = useMemo(() => {
    if (!departments.length || !employees.list.length) return [];
    const counts = new Map();
    departments.forEach(d => counts.set(d.id || d._id || d.name, { name: d.name || d.label || 'Sans nom', value: 0 }));
    let unaffected = 0;
    employees.list.forEach(e => {
      const did = e.departmentId || e.department || null;
      if (did && counts.has(did)) {
        counts.get(did).value += 1;
      } else {
        unaffected += 1;
      }
    });
    const arr = Array.from(counts.values()).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
    if (unaffected > 0) arr.push({ name: 'Non rattachés', value: unaffected });
    return arr;
  }, [departments, employees.list]);

  // Évolution réelle des effectifs sur 12 mois (basée sur dateEmbauche / hireDate)
  const headcountSeries = useMemo(() => {
    if (!employees.list.length) return [];
    // Pour chaque mois M (M-11..M0), compter les employés dont dateEmbauche <= fin du mois ET (pas de dateSortie OU dateSortie > fin du mois)
    const series = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const ref = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59); // dernier jour du mois
      let count = 0;
      for (const e of employees.list) {
        const hireRaw = e.dateEmbauche || e.hireDate || e.createdAt;
        if (!hireRaw) continue;
        const hire = new Date(hireRaw);
        if (Number.isNaN(hire.getTime()) || hire > ref) continue;
        const exitRaw = e.dateSortie || e.exitDate || null;
        if (exitRaw) {
          const exit = new Date(exitRaw);
          if (!Number.isNaN(exit.getTime()) && exit <= ref) continue;
        }
        count += 1;
      }
      series.push({
        m: ref.toLocaleString('fr-FR', { month: 'short', year: '2-digit' }),
        value: count,
      });
    }
    return series;
  }, [employees.list]);

  const totalCritical = (pendingActions.validations + pendingActions.evaluations + (pendingActions.contracts || 0));

  // Score de qualité des données: basé sur les vrais taux de renseignement
  const healthScore = useMemo(() => {
    if (!kpi || empCount === 0) return null;
    const sexe = kpi.tauxRenseignementSexe ?? null;
    const age = kpi.tauxRenseignementAge ?? null;
    const cat = kpi.tauxRenseignementCategorie ?? null;
    const vals = [sexe, age, cat].filter(v => v !== null && v !== undefined && !Number.isNaN(v));
    if (vals.length === 0) return null;
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [kpi, empCount]);

  if (user?.role === 'SuperAdmin' && !user?.companyId) return <Navigate to="/admin" replace />;
  if (user?.role === 'Employé') return <Navigate to="/my-dashboard" replace />;
  if (user?.role === 'Manager') return <Navigate to="/manager-dashboard" replace />;

  // Insights IA réels : générés uniquement depuis les vraies KPIs (aucune valeur fictive)
  const aiInsights = [];
  if (kpi && empCount > 0) {
    if (typeof kpi.tauxTurnover === 'number' && kpi.tauxTurnover >= 15) {
      aiInsights.push({
        icon: TrendingDown, iconBg: 'bg-red-50', iconColor: 'text-red-600',
        text: <>Turnover annualisé élevé : <span className="font-semibold">{kpi.tauxTurnover}%</span>.</>,
        href: '/kpi',
      });
    }
    if (typeof kpi.tauxRenseignementSexe === 'number' && kpi.tauxRenseignementSexe < 80) {
      aiInsights.push({
        icon: Activity, iconBg: 'bg-amber-50', iconColor: 'text-amber-600',
        text: <>Le sexe est renseigné pour <span className="font-semibold">{kpi.tauxRenseignementSexe}%</span> seulement des employés actifs.</>,
        href: '/employees',
      });
    }
    if (pendingActions.evaluations > 0) {
      aiInsights.push({
        icon: Clock, iconBg: 'bg-blue-50', iconColor: 'text-blue-600',
        text: <><span className="font-semibold">{pendingActions.evaluations} évaluation{pendingActions.evaluations > 1 ? 's' : ''}</span> nécessite{pendingActions.evaluations > 1 ? 'nt' : ''} votre validation.</>,
        href: '/evaluation-campaigns',
      });
    }
  }

  const priorityItems = [
    { icon: ClipboardCheck, color: 'text-emerald-700', bg: 'bg-emerald-50',
      badge: 'bg-emerald-100 text-emerald-700',
      label: 'Validations en attente', count: pendingActions.validations, href: '/absences/admin' },
    { icon: UserPlus, color: 'text-blue-700', bg: 'bg-blue-50',
      badge: 'bg-blue-100 text-blue-700',
      label: 'Onboarding en cours', count: pendingActions.onboarding, href: '/employees?status=Onboarding' },
    { icon: Star, color: 'text-red-700', bg: 'bg-red-50',
      badge: 'bg-red-100 text-red-700',
      label: 'Évaluations à valider', count: pendingActions.evaluations, href: '/evaluation-campaigns' },
  ].filter(it => it.count > 0);

  // Deltas réels mois courant (entrées nettes)
  const netNewEmployees = (kpi?.entreesMois ?? 0) - (kpi?.sortiesMois ?? 0);
  const empTrend = kpi ? `${netNewEmployees >= 0 ? '+' : ''}${netNewEmployees} ce mois` : null;
  const empTrendUp = netNewEmployees >= 0;

  // Taux d'activation des utilisateurs (réel)
  const activationPct = users.total > 0 ? Math.round((users.active / users.total) * 100) : null;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-7xl space-y-8" data-testid="executive-dashboard">
        {/* Hero */}
        <HeroBanner userName={user?.firstName || ''} healthScore={healthScore} criticalCount={totalCritical} />

        {/* KPI Row — sans sparkline simulée, sans foot factice */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard testId="kpi-employees" icon={Users} label="Employés" value={empCount.toLocaleString('fr-FR')}
            trend={empTrend} trendUp={empTrendUp} color="brand" />
          <KpiCard testId="kpi-departments" icon={Building2} label="Départements" value={deptCount}
            trend={null} color="ok" />
          <KpiCard testId="kpi-sites" icon={MapPin} label="Sites" value={siteCount}
            trend={null} color="warn" />
          <KpiCard testId="kpi-users" icon={UserCog} label="Utilisateurs" value={userCount}
            trend={null} color="ai"
            footLabel={activationPct !== null ? "Taux d'activation" : null}
            footValue={activationPct !== null ? `${activationPct}%` : null}
            footPct={activationPct} />
        </div>

        {/* Analytics + AI/Priority right column */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <HeadcountEvolution data={headcountSeries} employees={empCount} />
            <DepartmentDonut breakdown={deptBreakdown} />
          </div>
          <div className="space-y-6">
            <AIAssistantCard insights={aiInsights} />
            {priorityItems.length > 0 && <PriorityActionsList items={priorityItems} />}
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-ink-900">Actions rapides</h2>
              <p className="text-sm text-ink-500">Les modules les plus utilisés à portée de clic.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <QuickActionTile icon={Target} label="Lancer une évaluation" sub="Créer une campagne"
              to="/evaluation-campaigns" color="bg-blue-100 text-blue-700" />
            <QuickActionTile icon={Brain} label="Analyser les compétences" sub="Cartographier les talents"
              to="/skills/cockpit" color="bg-violet-100 text-violet-700" />
            <QuickActionTile icon={Sparkles} label="Recommandations IA" sub="Actions prioritaires"
              to="/ai-insights" color="bg-amber-100 text-amber-700" />
            <QuickActionTile icon={UserPlus} label="Nouvel employé" sub="Ajouter un collaborateur"
              to="/employees/new" color="bg-emerald-100 text-emerald-700" />
            <QuickActionTile icon={Upload} label="Importer des données" sub="CSV, Excel, SIRH"
              to="/import" color="bg-slate-100 text-slate-700" />
            <QuickActionTile icon={FileText} label="Exporter un rapport" sub="Synthèse DG"
              to="/executive-view" color="bg-indigo-100 text-indigo-700" />
          </div>
        </div>

        {/* Tendances clés — uniquement les vraies KPIs calculées */}
        {kpi && empCount > 0 && (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold tracking-tight text-ink-900">Tendances clés</h2>
              <p className="text-sm text-ink-500">Indicateurs annualisés calculés sur le mois en cours.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <TrendKpi testId="trend-turnover" icon={TrendingDown} label="Turnover (annualisé)"
                value={fmtPct(kpi.tauxTurnover)} color="brand" />
              <TrendKpi testId="trend-demissions" icon={TrendingUp} label="Démissions (annualisé)"
                value={fmtPct(kpi.tauxDemission)} color="warn" />
              <TrendKpi testId="trend-embauche" icon={UserPlus} label="Embauches (annualisé)"
                value={fmtPct(kpi.tauxEmbauche)} color="ok" />
              <TrendKpi testId="trend-encadrement" icon={BarChart3} label="Taux d'encadrement"
                value={fmtPct(kpi.tauxEncadrement)} color="brand" />
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center text-xs text-ink-400 py-3" data-testid="dashboard-loading">
            Synchronisation des indicateurs…
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
